import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import { useEditor } from '../../context/EditorContext';
import { DesignCanvas } from './DesignCanvas';
import { TSHIRT_COLORS, VIEWS, getTShirtImage, preloadImages } from '../../config/productImages';
import { HelpCircle } from 'lucide-react';
import { PrintGuidePanel } from './PrintGuidePanel';
import { ViewThumbnails } from './preview/ViewThumbnails';
import { ColorSelector } from './preview/ColorSelector';
import { QuantityPanel } from './preview/QuantityPanel';
import { PricingGuide } from './preview/PricingGuide';
import { logger } from '../../utils/logger';

// Logical canvas coordinate system, pinned 1:1 over the 600x800 mockup image (2/3 ratio)
const CANVAS_SIZE = { width: 400, height: 533 };

const VIEW_LABELS = {
  front: 'Front',
  back: 'Back',
  left: 'Left Sleeve',
  right: 'Right Sleeve',
};

export const SpreadshirtPreview = forwardRef(({ quantity, setQuantity, unitPrice, onAddToCart, isDesigning, onStartDesigning }, ref) => {
  const { color, setColor, view, setView, getCurrentElements, frontElements, backElements, leftElements, rightElements } = useEditor();
  const [imageLoaded, setImageLoaded] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showGuidePanel, setShowGuidePanel] = useState(false);
  const [canvasScale, setCanvasScale] = useState(1);
  const mockupBoxRef = useRef(null);

  // Keep the design canvas scaled 1:1 with the rendered mockup so the
  // on-screen design position/size always matches the exported print file
  useEffect(() => {
    const el = mockupBoxRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      if (w > 0) setCanvasScale(w / CANVAS_SIZE.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const canvasRefs = {
    front: useRef(null),
    back: useRef(null),
    left: useRef(null),
    right: useRef(null),
  };

  const currentColor = useMemo(() =>
    TSHIRT_COLORS.find(c => c.hex === color) || TSHIRT_COLORS[0],
    [color]
  );

  const currentImage = useMemo(() =>
    getTShirtImage(currentColor.id, view),
    [currentColor.id, view]
  );

  const elementsByView = { front: frontElements, back: backElements, left: leftElements, right: rightElements };

  // Expose canvas refs and export methods to parent
  useImperativeHandle(ref, () => ({
    frontCanvas: canvasRefs.front.current,
    backCanvas: canvasRefs.back.current,
    leftCanvas: canvasRefs.left.current,
    rightCanvas: canvasRefs.right.current,
    // Export all designs as high-resolution print files (cropped to print regions)
    exportAllDesigns: () => {
      const exports = {
        front_design_data: null,
        back_design_data: null,
        left_design_data: null,
        right_design_data: null,
      };

      VIEWS.forEach((v) => {
        if (!elementsByView[v]?.length) return;
        try {
          const canvas = canvasRefs[v].current;
          if (canvas?.exportCanvas) {
            exports[`${v}_design_data`] = canvas.exportCanvas();
          }
        } catch (err) {
          logger.error(`Failed to export ${v} design:`, err);
        }
      });

      return exports;
    },
  }));

  // Preload images when color changes
  useEffect(() => {
    preloadImages(currentColor.id);
  }, [currentColor.id]);

  const handleColorChange = (newColor) => {
    if (newColor.hex === color) return;
    setIsTransitioning(true);
    setImageLoaded(false);
    preloadImages(newColor.id);
    setTimeout(() => {
      setColor(newColor.hex);
      setTimeout(() => setIsTransitioning(false), 100);
    }, 100);
  };

  const handleViewChange = (newView) => {
    if (newView === view) return;
    setIsTransitioning(true);
    setImageLoaded(false);
    setTimeout(() => {
      setView(newView);
      setTimeout(() => setIsTransitioning(false), 100);
    }, 100);
  };

  const currentElements = getCurrentElements();
  const currentViewHasDesigns = currentElements.length > 0;

  const viewDesignCounts = {
    front: frontElements?.length || 0,
    back: backElements?.length || 0,
    left: leftElements?.length || 0,
    right: rightElements?.length || 0,
  };

  return (
    <div className="flex flex-col items-center w-full">
      {/* Product Preview Container */}
      <div className="relative w-full max-w-md">
        <div className="relative aspect-[3/4] rounded-2xl bg-gradient-to-b from-gray-100 to-gray-50 shadow-xl border border-gray-200">
          {/* Loading Overlay */}
          {isTransitioning && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-30 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* T-Shirt Image */}
          <div className="relative w-full h-full flex items-center justify-center p-4">
            {/* Mockup box: 3:4 like the 600x800 mockup image, canvas pinned to it */}
            <div ref={mockupBoxRef} className="relative w-full aspect-[3/4]">
              <img
                key={`${currentColor.id}-${view}`}
                src={currentImage}
                alt={`${currentColor.name} T-shirt - ${view} view`}
                className={`w-full h-full object-contain transition-opacity duration-200 ${
                  imageLoaded && !isTransitioning ? 'opacity-100' : 'opacity-0'
                }`}
                onLoad={() => setImageLoaded(true)}
                crossOrigin="anonymous"
              />

              {/* "Add Your Design" Overlay - Only on mobile when current view has no designs */}
              {imageLoaded && !currentViewHasDesigns && (
                <button
                  onClick={onStartDesigning}
                  className="lg:hidden absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/20 backdrop-blur-[2px] transition-all active:bg-black/30"
                >
                  <div className="bg-white rounded-2xl px-6 py-4 shadow-xl flex flex-col items-center">
                    <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mb-3">
                      <svg className="w-7 h-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <span className="text-lg font-bold text-gray-800">Tap to Add Design</span>
                    <span className="text-sm text-gray-500 mt-1">Text or Image</span>
                  </div>
                </button>
              )}

              {/* Design Canvas Overlay */}
              {/* CRITICAL: Render ALL 4 canvases so refs are valid for export */}
              {/* Use visibility:hidden instead of display:none - mobile browsers don't render display:none canvases */}
              {imageLoaded && (
                <div className="absolute inset-0 z-20 cursor-pointer">
                  {VIEWS.map((v) => (
                    <div
                      key={v}
                      style={{
                        position: view === v ? 'relative' : 'absolute',
                        visibility: view === v ? 'visible' : 'hidden',
                        top: 0,
                        left: 0,
                        pointerEvents: view === v ? 'auto' : 'none',
                      }}
                    >
                      <DesignCanvas
                        ref={canvasRefs[v]}
                        width={CANVAS_SIZE.width}
                        height={CANVAS_SIZE.height}
                        view={v}
                        scale={canvasScale}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Badges */}
          <div className="absolute top-3 left-3 z-30">
            <div className="flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-lg px-2.5 py-1.5 shadow-md">
              <div
                className="w-4 h-4 rounded-full border border-gray-300"
                style={{ backgroundColor: currentColor.hex }}
              />
              <span className="text-xs font-semibold text-gray-700">{currentColor.name}</span>
            </div>
          </div>

          <div className="absolute top-3 right-3 z-30">
            <div className="bg-white/95 backdrop-blur-sm rounded-lg px-2.5 py-1.5 shadow-md">
              <span className="text-xs font-semibold text-gray-700">{VIEW_LABELS[view]}</span>
            </div>
          </div>

          {/* Print Guide Button */}
          <button
            onClick={() => setShowGuidePanel(true)}
            className="absolute bottom-14 left-3 z-30 bg-white/95 backdrop-blur-sm rounded-lg px-2.5 py-1.5 shadow-md text-xs font-medium text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center gap-1.5"
            data-testid="show-guide-btn"
          >
            <HelpCircle className="w-3.5 h-3.5 text-cyan-500" />
            Print Guide
          </button>

          {/* Element count badge - Tap to edit designs */}
          {currentElements.length > 0 && (
            <button
              onClick={onStartDesigning}
              className="lg:hidden absolute bottom-3 right-3 z-30 touch-manipulation"
              data-testid="design-count-badge"
            >
              <div className="bg-primary text-white rounded-full px-3 py-1.5 shadow-md flex items-center gap-1.5 active:scale-95 transition-transform">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                <span className="text-xs font-bold">{currentElements.length} design{currentElements.length > 1 ? 's' : ''}</span>
              </div>
            </button>
          )}
        </div>

        <ViewThumbnails
          view={view}
          colorId={currentColor.id}
          viewDesignCounts={viewDesignCounts}
          isTransitioning={isTransitioning}
          onViewChange={handleViewChange}
        />
      </div>

      <ColorSelector color={color} isTransitioning={isTransitioning} onColorChange={handleColorChange} />

      <QuantityPanel quantity={quantity} setQuantity={setQuantity} unitPrice={unitPrice} onAddToCart={onAddToCart} />

      <PricingGuide />

      <PrintGuidePanel
        isOpen={showGuidePanel}
        onClose={() => setShowGuidePanel(false)}
        currentView={view}
      />
    </div>
  );
});

SpreadshirtPreview.displayName = 'SpreadshirtPreview';

export default SpreadshirtPreview;
